import React, { useState, useEffect } from 'react';
import {
  Percent,
  Plus,
  Trash2,
  Edit,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  DollarSign,
  Settings,
  ToggleLeft,
  ToggleRight,
  RefreshCw
} from 'lucide-react';

interface DiscountTier {
  id: number;
  minAmount: number;
  discountRate: number;
  active: boolean;
}

interface DiscountStats {
  totalDiscountGiven: number;
  totalTransactionsWithDiscount: number;
  averageDiscountRate: number;
  systemEnabled: boolean;
}

const DiscountManagementPage: React.FC = () => {
  const [discountTiers, setDiscountTiers] = useState<DiscountTier[]>([]);
  const [stats, setStats] = useState<DiscountStats>({
    totalDiscountGiven: 0,
    totalTransactionsWithDiscount: 0,
    averageDiscountRate: 0,
    systemEnabled: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTier, setEditingTier] = useState<DiscountTier | null>(null);
  
  // 새 할인 단계 추가 폼
  const [newTier, setNewTier] = useState({
    minAmount: '',
    discountRate: ''
  });

  useEffect(() => {
    loadDiscountData();
  }, []);

  const loadDiscountData = async () => {
    try {
      setLoading(true);
      
      // Mock 데이터 - 실제로는 스마트 컨트랙트에서 가져옴
      const mockTiers: DiscountTier[] = [
        { id: 1, minAmount: 1000, discountRate: 5, active: true },
        { id: 2, minAmount: 5000, discountRate: 10, active: true },
        { id: 3, minAmount: 10000, discountRate: 15, active: true },
        { id: 4, minAmount: 50000, discountRate: 20, active: false }
      ];
      
      const mockStats: DiscountStats = {
        totalDiscountGiven: 125000,
        totalTransactionsWithDiscount: 342,
        averageDiscountRate: 12.5,
        systemEnabled: true
      };
      
      setDiscountTiers(mockTiers);
      setStats(mockStats);
      
      // 실제 구현 시:
      // const response = await fetch('/api/admin/discount-tiers');
      // const data = await response.json();
      // setDiscountTiers(data.tiers);
      // setStats(data.stats);
      
    } catch (error) {
      console.error('할인 데이터 로드 실패:', error);
      setError('할인 데이터를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDiscountTier = async () => {
    try {
      const minAmount = parseFloat(newTier.minAmount);
      const discountRate = parseFloat(newTier.discountRate);
      
      if (minAmount <= 0 || discountRate <= 0 || discountRate > 50) {
        setError('올바른 값을 입력해주세요. (할인율: 1-50%)');
        return;
      }
      
      setLoading(true);
      
      // 실제 구현 시 스마트 컨트랙트 호출:
      // const contract = new ethers.Contract(contractAddress, abi, signer);
      // const tx = await contract.addDiscountTier(
      //   ethers.parseUnits(minAmount.toString(), 6), // USDT 6 decimals
      //   discountRate * 100 // basis points
      // );
      // await tx.wait();
      
      // Mock 구현
      const newId = Math.max(...discountTiers.map(t => t.id), 0) + 1;
      const newDiscountTier: DiscountTier = {
        id: newId,
        minAmount,
        discountRate,
        active: true
      };
      
      setDiscountTiers(prev => [...prev, newDiscountTier].sort((a, b) => a.minAmount - b.minAmount));
      setNewTier({ minAmount: '', discountRate: '' });
      setShowAddModal(false);
      setSuccess('할인 단계가 추가되었습니다.');
      
    } catch (error) {
      console.error('할인 단계 추가 실패:', error);
      setError('할인 단계 추가에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDiscountTier = async (tierId: number) => {
    if (!window.confirm('이 할인 단계를 삭제하시겠습니까?')) return;
    
    try {
      setLoading(true);
      
      // 실제 구현 시:
      // const tierIndex = discountTiers.findIndex(t => t.id === tierId);
      // const tx = await contract.removeDiscountTier(tierIndex);
      // await tx.wait();
      
      // Mock 구현
      setDiscountTiers(prev => prev.filter(tier => tier.id !== tierId));
      setSuccess('할인 단계가 삭제되었습니다.');
      
    } catch (error) {
      console.error('할인 단계 삭제 실패:', error);
      setError('할인 단계 삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSystem = async () => {
    try {
      setLoading(true);
      
      // 실제 구현 시:
      // const tx = await contract.toggleDiscountSystem();
      // await tx.wait();
      
      // Mock 구현
      setStats(prev => ({ ...prev, systemEnabled: !prev.systemEnabled }));
      setSuccess(`할인 시스템이 ${stats.systemEnabled ? '비활성화' : '활성화'}되었습니다.`);
      
    } catch (error) {
      console.error('할인 시스템 토글 실패:', error);
      setError('할인 시스템 설정 변경에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleClearAllTiers = async () => {
    if (!window.confirm('모든 할인 단계를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;
    
    try {
      setLoading(true);
      
      // 실제 구현 시:
      // const tx = await contract.clearAllDiscountTiers();
      // await tx.wait();
      
      // Mock 구현
      setDiscountTiers([]);
      setSuccess('모든 할인 단계가 삭제되었습니다.');
      
    } catch (error) {
      console.error('할인 단계 전체 삭제 실패:', error);
      setError('할인 단계 전체 삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString('ko-KR');
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Percent className="w-8 h-8 text-orange-500 mr-3" />
            할인율 관리
          </h1>
          <p className="text-gray-600 mt-2">VTTN 구매 할인 시스템을 관리합니다</p>
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={loadDiscountData}
            disabled={loading}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>새로고침</span>
          </button>
          
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>할인 단계 추가</span>
          </button>
        </div>
      </div>

      {/* 에러/성공 메시지 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-green-700">{success}</span>
          </div>
        </div>
      )}

      {/* 시스템 상태 및 통계 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">시스템 상태</p>
              <p className={`text-lg font-bold ${stats.systemEnabled ? 'text-green-600' : 'text-red-600'}`}>
                {stats.systemEnabled ? '활성화' : '비활성화'}
              </p>
            </div>
            <button
              onClick={handleToggleSystem}
              disabled={loading}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {stats.systemEnabled ? (
                <ToggleRight className="w-6 h-6 text-green-500" />
              ) : (
                <ToggleLeft className="w-6 h-6 text-gray-400" />
              )}
            </button>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <DollarSign className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-sm text-gray-600">총 할인 지급</p>
              <p className="text-lg font-bold text-gray-900">
                {formatNumber(stats.totalDiscountGiven)} VTTN
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <Settings className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-sm text-gray-600">할인 거래 수</p>
              <p className="text-lg font-bold text-gray-900">
                {formatNumber(stats.totalTransactionsWithDiscount)}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <Percent className="w-8 h-8 text-orange-500" />
            <div>
              <p className="text-sm text-gray-600">평균 할인율</p>
              <p className="text-lg font-bold text-gray-900">
                {stats.averageDiscountRate}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 할인 단계 목록 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">할인 단계 목록</h2>
            <button
              onClick={handleClearAllTiers}
              disabled={loading || discountTiers.length === 0}
              className="px-3 py-1 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              전체 삭제
            </button>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  최소 구매 금액
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  할인율
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  상태
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  작업
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {discountTiers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                    설정된 할인 단계가 없습니다.
                  </td>
                </tr>
              ) : (
                discountTiers.map((tier) => (
                  <tr key={tier.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        ${formatNumber(tier.minAmount)} USDT 이상
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {tier.discountRate}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        tier.active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {tier.active ? '활성' : '비활성'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setEditingTier(tier)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleRemoveDiscountTier(tier.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 할인 단계 추가 모달 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">할인 단계 추가</h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewTier({ minAmount: '', discountRate: '' });
                  setError('');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  최소 구매 금액 (USDT)
                </label>
                <input
                  type="number"
                  value={newTier.minAmount}
                  onChange={(e) => setNewTier(prev => ({ ...prev, minAmount: e.target.value }))}
                  placeholder="예: 1000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  할인율 (%)
                </label>
                <input
                  type="number"
                  value={newTier.discountRate}
                  onChange={(e) => setNewTier(prev => ({ ...prev, discountRate: e.target.value }))}
                  placeholder="예: 5"
                  min="1"
                  max="50"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-700">
                  💡 할인율은 1-50% 사이로 설정해주세요.
                </p>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewTier({ minAmount: '', discountRate: '' });
                  setError('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleAddDiscountTier}
                disabled={loading || !newTier.minAmount || !newTier.discountRate}
                className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>추가</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountManagementPage;